import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { ALL_NAV_TABS, type NavTabId } from "@/hooks/useCompanyNavTabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";

const FEATURE_TABS = ALL_NAV_TABS.filter(
  (id): id is Exclude<NavTabId, "profile" | "admin"> =>
    id !== "profile" && id !== "admin",
);

const TAB_LABELS: Record<
  Exclude<NavTabId, "profile" | "admin">,
  { title: string; description: string }
> = {
  "work-orders": { title: "Work Orders", description: "List of active and historical work orders." },
  inventory: { title: "Inventory", description: "Harvested parts and bin storage." },
  search: { title: "Search", description: "Home / appliance search." },
  scanner: { title: "Pre Triage", description: "Camera-based device pre-triage scanner." },
  store: { title: "Store", description: "Refurbished appliance store." },
};

interface Company {
  id: string;
  name: string;
}

export default function NavigationAdmin({ fixedCompanyId, fixedCompanyName }: { fixedCompanyId?: string; fixedCompanyName?: string } = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isSuperadmin: isAdmin, isLoading } = useIsSuperadmin();
  const { isSuperadmin } = useIsSuperadmin();
  const { id: currentCompanyId, name: currentCompanyName } = useCurrentCompany();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("companies").select("id, name").order("name");
      setCompanies((data as Company[]) || []);
    })();
  }, []);

  useEffect(() => {
    if (!selectedCompanyId && currentCompanyId) {
      setSelectedCompanyId(currentCompanyId);
    }
  }, [currentCompanyId, selectedCompanyId]);

  const companyId = fixedCompanyId ?? selectedCompanyId ?? currentCompanyId;
  const companyName =
    fixedCompanyName ?? companies.find((c) => c.id === companyId)?.name ?? currentCompanyName ?? null;

  const { data: toggles } = useQuery({
    queryKey: ["company_nav_tabs", companyId, "admin"],
    enabled: !!companyId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("company_nav_tabs")
        .select("tab_key, enabled")
        .eq("company_id", companyId);
      const map = new Map<string, boolean>();
      for (const r of (data ?? []) as { tab_key: string; enabled: boolean }[]) {
        map.set(r.tab_key, r.enabled);
      }
      return map;
    },
  });

  if (isLoading) return null;
  // Access enforced by the route guard in App.tsx.

  const isEnabled = (id: Exclude<NavTabId, "profile" | "admin">) => {
    const v = toggles?.get(id);
    return v === undefined ? true : v;
  };

  const setEnabled = async (id: NavTabId, enabled: boolean) => {
    if (!companyId) return toast.error("No company selected");
    const { error } = await (supabase as any)
      .from("company_nav_tabs")
      .upsert(
        { company_id: companyId, tab_key: id, enabled },
        { onConflict: "company_id,tab_key" },
      );
    if (error) return toast.error(error.message);
    toast.success(`${TAB_LABELS[id].title} ${enabled ? "enabled" : "disabled"} for ${companyName ?? "this company"}`);
    qc.invalidateQueries({ queryKey: ["company_nav_tabs", companyId] });
    qc.invalidateQueries({ queryKey: ["company_nav_tabs", companyId, "admin"] });
  };

  return (
    <div className={fixedCompanyId ? "pt-4" : "min-h-screen bg-background pb-24"}>
      <div className={fixedCompanyId ? "pb-3" : "px-6 pt-6 pb-3"}>
        {!fixedCompanyId && <SettingsBackLink to="/platform/companies" label="Companies" />}
        <div>
          <h1 className="text-2xl font-bold">App Features</h1>
          <p className="text-sm text-muted-foreground">
            Choose which features are available in the app for {companyName ?? "your company"}. Hidden features disappear from navigation and direct links redirect to an available feature.
          </p>
        </div>
      </div>

      {isSuperadmin && !fixedCompanyId && (
        <div className="px-6 mb-4">
          <SearchableSelect
            value={selectedCompanyId ?? null}
            onChange={(val) => setSelectedCompanyId(val ?? "")}
            placeholder="Select a company"
            searchPlaceholder="Search companies…"
            emptyMessage="No companies found"
            items={companies.map((c) => ({ value: c.id, label: c.name }))}
          />
        </div>
      )}

      <div className={fixedCompanyId ? "space-y-3" : "px-6 space-y-3"}>
        {FEATURE_TABS.map((id) => {
          const enabled = isEnabled(id);
          return (
            <Card key={id} className="p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="font-medium">{TAB_LABELS[id].title}</div>
                <div className="text-xs text-muted-foreground">{TAB_LABELS[id].description}</div>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor={`nav-${id}`} className="text-sm text-muted-foreground">
                  Enabled
                </Label>
                <Switch
                  id={`nav-${id}`}
                  checked={enabled}
                  onCheckedChange={(v) => setEnabled(id, v)}
                  disabled={!companyId}
                />
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
